import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { Table, Form, Button, Pagination, Row, Col } from 'react-bootstrap';
import { Link, useHistory, useLocation } from 'react-router-dom';
import api from '../utils/api';
import { DateRangeContext } from '../context/DateRangeContext';
import useAuth from '../context/useAuth';

const Receipts = () => {
    const { dateRange, customStartDate, customEndDate, setDateRange, setCustomStartDate, setCustomEndDate } = useContext(DateRangeContext);
    const [receipts, setReceipts] = useState([]);
    const [paymentBreakdown, setPaymentBreakdown] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalDonation, setTotalDonation] = useState(0);
    const [selectedReceipt, setSelectedReceipt] = useState(null);
    const [sortOrder, setSortOrder] = useState('DESC');
    const history = useHistory();
    const location = useLocation();
    const { resultsPerPage } = useAuth();
    const receiptsPerPage = parseInt(resultsPerPage, 10);

    const setDropdownWidth = useCallback(() => {
        const selectElement = document.getElementById('dateRangeSelect');
        if (selectElement) {
            const options = selectElement.options;
            let maxWidth = 0;

            for (let i = 0; i < options.length; i++) {
                const optionWidth = getTextWidth(options[i].text, window.getComputedStyle(selectElement).font);
                if (optionWidth > maxWidth) {
                    maxWidth = optionWidth;
                }
            }

            selectElement.style.width = `${maxWidth + 50}px`;
        }
    }, []);

    const getReceipts = useCallback(() => {
        setLoading(true);
        console.log('currentPage from getReceipts: ', currentPage);
        const selectedDateRange = dateRange; // Ensure using updated state
        let path = `/api/receipts?dateRange=${selectedDateRange}&page=${currentPage}&limit=${receiptsPerPage}&sortOrder=${sortOrder}`;
        if (dateRange === 'custom') {
            path = `/api/receipts?dateRange=${selectedDateRange}&startDate=${customStartDate}&endDate=${customEndDate}&page=${currentPage}&limit=${receiptsPerPage}&sortOrder=${sortOrder}`;
        }
        api.get(path)
            .then(res => {
                const { data, totalPages, totalDonation, paymentBreakdown } = res.data;
                setReceipts(data);
                setTotalPages(totalPages);
                setTotalDonation(totalDonation);
                setPaymentBreakdown(paymentBreakdown);
                setLoading(false);
            })
            .catch(error => {
                if (error.response && (error.response.status === 401 || error.response.status === 403)) {
                    console.log('User is not authenticated or token expired. Redirecting to login page.');
                    history.push('/login');
                } else {
                    setError(error.message);
                    setLoading(false);
                    console.error('Error fetching receipts:', error);
                }
            });
    }, [dateRange, currentPage, receiptsPerPage, sortOrder, customStartDate, customEndDate, history]); // Added currentPage

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const pageNumber = parseInt(queryParams.get('page'), 10);

        if (location.state?.fromPage) {
            setCurrentPage(location.state.fromPage); // Prioritize fromPage
            console.log('page number from useEffect location search fromPage: ', location.state.fromPage);
        } else if (!isNaN(pageNumber) && pageNumber !== currentPage) {
            setCurrentPage(pageNumber);
            console.log('page number from useEffect location search pagenumber: ', pageNumber);
        }

    }, [location.search, location.state, currentPage]);

    useEffect(() => {
        setDropdownWidth();
    });

    useEffect(() => {
        if (!hasUserChangedDateRange.current) return; // Skip reset if not user-triggered

        if (dateRange && dateRange !== 'custom') {
            setCurrentPage(1);  // Reset to first page only if user changes date range

            console.log('UseEffect -Resetting to page 1 due to dateRange change');
        }
    }, [dateRange]);



    useEffect(() => {
        getReceipts();
    }, [getReceipts]); // Already depends on currentPage




    const getTextWidth = (text, font) => {
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        context.font = font;
        return context.measureText(text).width;
    };

    const hasUserChangedDateRange = useRef(false);

    const handleDateRangeChange = (event) => {
        setDateRange(event.target.value);
        hasUserChangedDateRange.current = true; // Mark as user-triggered
        history.push(`?page=1`); // Update the URL parameter
        setCurrentPage(1);  // Reset to first page only if user changes date range
    };



    const handleCustomDateChange = (event) => {
        const { name, value } = event.target;
        if (name === 'customStartDate') {
            setCustomStartDate(value);
        } else if (name === 'customEndDate') {
            setCustomEndDate(value);
        }
    };

    const handleCustomDateSubmit = () => {
        getReceipts();
    };
    const handlePageChange = (pageNumber) => {
        if (pageNumber !== currentPage) {
            history.push(`?page=${pageNumber}`);
        }
    };


    const handleRowClick = (receipt) => {
        setSelectedReceipt(receipt);
    };

    const handlePrintCurrentPage = () => {
        window.print();
    };

    const handleSortOrderChange = (event) => {
        setSortOrder(event.target.value);
        getReceipts();
    };

    const getDateRangeLabel = (dateRange) => {
        switch (dateRange) {
            case 'all':
                return 'All';
            case 'currentYear':
                return 'Current Year';
            case 'currentMonth':
                return 'Current Month';
            case 'currentWeek':
                return 'Current Week';
            case 'currentDay':
                return 'Current Day';
            case 'lastYear':
                return 'Last Year';
            case 'lastMonth':
                return 'Last Month';
            case 'lastWeek':
                return 'Last Week';
            case 'lastDay':
                return 'Last Day';
            case 'custom':
                return 'Custom';
            default:
                return 'All'; // Default case if no match
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return ''; // Return empty if invalid date
        const [year, month, day] = dateStr.split('-');
        return `${month}/${day}/${year}`; // Return formatted as MM/DD/YYYY
    };

    if (loading) return <div>Loading...</div>;
    if (error) return <div>Error: {error}</div>;

    const donations = receipts.map((receipt) => (
        <tr key={receipt.receiptNum} className={receipt === selectedReceipt ? 'selected-row' : ''} onClick={() => handleRowClick(receipt)}>
            <td className="align-middle">
                <Link
                    to={{
                        pathname: `/receiptDetails/${receipt.receiptNum}`,
                        search: `?page=${currentPage}`,
                        state: { fromPage: currentPage }
                    }}
                    className="receipt-link"
                >
                    {receipt.receiptNum}
                </Link>
            </td>
            <td className="align-middle">{formatDate(receipt.receiptDate)}</td>
            <td className="align-middle">{receipt.memberId}</td>
            <td className="align-middle">{receipt.first1}</td>
            <td className="align-middle">{receipt.last1}</td>
            <td className="align-middle">{receipt.city}</td>
            <td className="align-middle">
                {receipt?.email ? (
                    <Link
                        to={`/receiptDetails/${receipt.receiptNum}?page=${currentPage}&triggerEmail=true`}
                        className="receipt-link"
                    >
                        Y
                    </Link>
                ) : ''}
            </td>
            <td className="align-middle">
                {(() => {
                    const paymentModes = {
                        1: "Cash",
                        2: "Check",
                        3: "Credit Card",
                        4: "Bank Direct Deposit",
                        5: "Other",
                    };
                    return paymentModes[receipt.modeOfPayment] || "Unknown";
                })()}
            </td>
            <td className="text-sm-right align-middle">
                {receipt.status === 'V' ? 'Void' : `$${parseFloat(receipt.donationAmt || 0).toFixed(2)}`}
            </td>
        </tr>
    ));

    const maxPageNumbersToShow = 10;
    const currentGroup = Math.ceil(currentPage / maxPageNumbersToShow);
    const startPage = (currentGroup - 1) * maxPageNumbersToShow + 1;
    const endPage = Math.min(startPage + maxPageNumbersToShow - 1, totalPages);

    return (
        <div>
            <Button className="btn btn-primary float-right no-print small-font" onClick={handlePrintCurrentPage}>
                Print
            </Button>
            <Form.Group className="no-print">
                <Form.Row className="align-items-center">
                    <Col md={6} className="d-flex align-items-center">
                        <Form.Label className="small-font date-range-dropdown font-weight-bold mr-2">Date Range</Form.Label>
                        <Form.Control as="select" id="dateRangeSelect" value={dateRange} onChange={handleDateRangeChange} className="small-font">
                            <option value="all">All</option>
                            <option value="currentYear">Current Year</option>
                            <option value="currentMonth">Current Month</option>
                            <option value="currentWeek">Current Week</option>
                            <option value="currentDay">Current Day</option>
                            <option value="lastYear">Last Year</option>
                            <option value="lastMonth">Last Month</option>
                            <option value="lastWeek">Last Week</option>
                            <option value="lastDay">Last Day</option>
                            <option value="custom">Custom</option>
                        </Form.Control>
                    </Col>
                    <Col md={6} className="d-flex align-items-center">
                        <Form.Label className="small-font font-weight-bold mr-2">Sort by Receipt Date</Form.Label>
                        <div className="radio-box p-2">
                            <Form.Check
                                type="radio"
                                id="sort-desc"
                                name="sortOrder"
                                value="DESC"
                                label="Descending"
                                checked={sortOrder === 'DESC'}
                                onChange={handleSortOrderChange}
                                className="small-font"
                                inline
                            />
                            <Form.Check
                                type="radio"
                                id="sort-asc"
                                name="sortOrder"
                                value="ASC"
                                label="Ascending"
                                checked={sortOrder === 'ASC'}
                                onChange={handleSortOrderChange}
                                className="small-font"
                                inline
                            />
                        </div>
                    </Col>
                </Form.Row>
            </Form.Group>
            {dateRange === 'custom' && (
                <div className="no-print">
                    <Row>
                        <Col>
                            <Form.Group controlId="customStartDate">
                                <Form.Label className="small-font font-weight-bold">Start Date</Form.Label>
                                <Form.Control
                                    type="date"
                                    name="customStartDate" // Fixed this line by removing the extra 'name'
                                    value={customStartDate}
                                    onChange={handleCustomDateChange}
                                    className="small-input"
                                />
                            </Form.Group>
                        </Col>
                        <Col>
                            <Form.Group controlId="customEndDate">
                                <Form.Label className="small-font font-weight-bold">End Date</Form.Label>
                                <Form.Control
                                    type="date"
                                    name="customEndDate"
                                    value={customEndDate}
                                    onChange={handleCustomDateChange}
                                    className="small-input"
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Button
                                onClick={handleCustomDateSubmit}
                                className="small-font"
                            >
                                Apply
                            </Button>
                        </Col>
                    </Row>
                </div>
            )}
            <Table striped bordered hover size="sm" className="small-font">
                <thead>
                    <tr>
                        <th>Receipt Num</th>
                        <th>Receipt Date</th>
                        <th>Member ID</th>
                        <th>First Name</th>
                        <th>Last Name</th>
                        <th>City</th>
                        <th>Email Exists?</th>
                        <th>Mode of Payment</th>
                        <th>Amount</th>
                    </tr>
                </thead>
                <tbody>
                    {donations}
                </tbody>
            </Table>
            <p className="bold-text small-font">
                Total Donation for {getDateRangeLabel(dateRange)}:{" "}
                {new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                }).format(totalDonation || 0)}
            </p>
            {paymentBreakdown && paymentBreakdown.length > 0 && (
                <ul className="small-font">
                    {paymentBreakdown.map((item, index) => (
                        <li key={index}>
                            {item.mode} ({new Intl.NumberFormat("en-US").format(item.count)}):{" "}
                            {new Intl.NumberFormat("en-US", {
                                style: "currency",
                                currency: "USD",
                            }).format(item.total || 0)}
                        </li>
                    ))}
                </ul>
            )}
            <Pagination>
                <Pagination.First onClick={() => handlePageChange(1)} disabled={currentPage === 1} />
                <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />

                {startPage > 1 && (
                    <Pagination.Ellipsis onClick={() => handlePageChange(startPage - 1)} />
                )}

                {[...Array(endPage - startPage + 1)].map((_, index) => {
                    const pageNumber = startPage + index;
                    return (
                        <Pagination.Item
                            key={pageNumber}
                            active={pageNumber === currentPage}
                            onClick={() => handlePageChange(pageNumber)}
                        >
                            {pageNumber}
                        </Pagination.Item>
                    );
                })}

                {endPage < totalPages && (
                    <Pagination.Ellipsis onClick={() => handlePageChange(endPage + 1)} />
                )}

                <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
                <Pagination.Last onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} />
            </Pagination>
        </div>
    );
};

Receipts.propTypes = {
    history: PropTypes.object.isRequired,
    location: PropTypes.object.isRequired
};

export default Receipts;